import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { sendToApi } from 'api/client'
import { BrandVisualKit, Color } from 'types'
import { buildErrorMessage } from 'utils'
import { UseMutationOptions } from 'react-query/types/react/types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { reportError } from 'utils/error'

export interface Data {
  brand_visual_kit: BrandVisualKit
}

export type Options = Omit<UseMutationOptions<Data, unknown, Variables>, 'mutationKey' | 'mutationFn'>

interface Props {
  workspaceId: number
}

type Variables = {
  name?: string
  colors?: Color[]
  text_styles?: unknown[]
  brand_kit_id?: number
}

export const createBrandVisualKit = async ({ workspaceId, ...variables }: Props & Variables) => {
  const response = await sendToApi<Data>(`/w/${workspaceId}/brand_visual_kits`, {
    method: 'POST',
    body: { ...variables },
  })

  if (response.type !== SUCCESS_RESPONSE_TYPE) {
    reportError('Failed to create brand visual kit', {
      metaData: {
        variables,
        response: response.data,
      },
    })
    throw new Error(
      buildErrorMessage(response.data.errors) ||
        'An error occurred! Our team has been notified and we are looking into it.',
    )
  }
  return response.data
}

export const useCreateBrandVisualKit = (options?: Options) => {
  const currentWorkspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()
  return useMutation<Data, unknown, Variables>(
    'createBrandKit',
    async (variables) => {
      return await createBrandVisualKit({ workspaceId: currentWorkspaceId, ...variables })
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        const { brand_kit_id } = variables

        queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brand_visual_kits', 'list'])

        if (brand_kit_id) {
          queryClient.invalidateQueries(['workspaces', currentWorkspaceId, 'brandKits', 'detail', brand_kit_id])
        }

        options?.onSuccess?.(data, variables, context)
      },
    },
  )
}
